import { FontFamily, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { PlusIcon } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import * as React from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';

interface DialogControlProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;
  /**
   * Callback to change the dialog open state
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Whether to hide the dialog's trigger button
   */
  hideTrigger: boolean;
}

interface CallToActionCardProps extends Omit<AriaButtonProps, 'onPress' | 'children'> {
  /**
   * The icon to display at the top of the card
   * @required
   * @type {React.ReactNode}
   */
  icon: React.ReactNode;
  /**
   * The title of the card
   * @required
   * @type {string}
   */
  title: string;
  /**
   * The subtitle/description of the card
   * @required
   * @type {string}
   */
  subtitle: string;
  /**
   * Render prop function that receives dialog control props
   * Used to render dialog components that will be controlled by the card
   * @optional
   * @type {(props: DialogControlProps) => React.ReactElement}
   */
  children?: (props: DialogControlProps) => React.ReactElement;
  /**
   * The ref to the button element
   * @type {React.RefObject<HTMLButtonElement>}
   */
  ref?: React.RefObject<HTMLButtonElement>;
}

/**
 * CallToActionCard is a clickable card component that displays an icon, title, and subtitle.
 * The entire card acts as a button and shows a hover state with inverted colors.
 * When disabled, displays a "COMING SOON" badge in the top right corner.
 *
 * Uses render props pattern to control dialog components. The card manages dialog state
 * and passes control props to the render function.
 *
 * @example
 * <CallToActionCard
 *   icon={<WebIcon />}
 *   title="Score a product page"
 *   subtitle="Quickly upload and score any image and generate a Vizit score."
 *   aria-label="Score a product page"
 * />
 *
 * @example With dialog (recommended usage)
 * <CallToActionCard
 *   icon={<WebIcon />}
 *   title="Score a product page"
 *   subtitle="Quickly upload and score any image and generate a Vizit score."
 *   aria-label="Score a product page "
 * >
 *   {({ isOpen, onOpenChange, hideTrigger }) => (
 *     <ProductImportDialog
 *       isOpen={isOpen}
 *       onOpenChange={onOpenChange}
 *       hideTrigger={hideTrigger}
 *       portfolioId={portfolioId}
 *       onSuccessfulImport={handleImport}
 *     />
 *   )}
 * </CallToActionCard>
 */
function CallToActionCard(props: CallToActionCardProps) {
  const { icon, title, subtitle, className, isDisabled, ref, children, ...other } = props;
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const handlePress = () => {
    if (!isDisabled) {
      setIsDialogOpen(true);
    }
  };

  const dialogElement = children?.({
    isOpen: isDialogOpen,
    onOpenChange: setIsDialogOpen,
    hideTrigger: true,
  });

  return (
    <>
      <AriaButton
        {...other}
        isDisabled={isDisabled}
        onPress={handlePress}
        ref={ref}
        className={cx(
          // Base layout and sizing
          'group relative flex w-full flex-col items-start justify-between',
          'h-[185px] px-8 pb-12 pt-8',
          // Default state - light background
          'bg-brand-primary-alt-light',
          // Hover state - dark background
          'transition-colors duration-200',
          'hover:bg-foreground-primary',
          // Focus states for accessibility
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
          // Disabled state
          'disabled:cursor-not-allowed disabled:opacity-20',
          className,
        )}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center justify-center overflow-hidden px-3">
            <div className="text-gray-600 transition-colors group-hover:text-white">{icon}</div>
          </div>

          <div
            className="flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden="true"
          >
            <PlusIcon size={20} color={colors['gray-utility']['400']} />
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-8 text-left max-w-[560px]">
          <Text
            elementType="h3"
            size={TextSize.Lg}
            weight={TextWeight.Medium}
            color={TextColor.Primary}
            className="w-full transition-colors group-hover:text-white"
            showEllipsis={false}
          >
            {title}
          </Text>

          <Text
            elementType="p"
            size={TextSize.Xs}
            fontFamily={FontFamily.Mono}
            color={TextColor.Tertiary}
            className="w-full transition-colors group-hover:text-purple-200"
            showEllipsis={false}
          >
            {subtitle}
          </Text>
        </div>

        {isDisabled && (
          <div className="absolute right-4 top-4 rounded bg-foreground-primary px-2 py-2">
            <Text
              elementType="span"
              size={TextSize.Xs}
              fontFamily={FontFamily.Mono}
              className="whitespace-nowrap text-white"
              showEllipsis={false}
            >
              COMING SOON
            </Text>
          </div>
        )}
      </AriaButton>
      {dialogElement && <div className="sr-only">{dialogElement}</div>}
    </>
  );
}

export { CallToActionCard };
export type { CallToActionCardProps, DialogControlProps };
